import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FeatherChevronUp, FeatherChevronDown, FeatherCheckCircle, FeatherAlertCircle, FeatherFileText, FeatherAlertTriangle, FeatherCheckSquare, FeatherUsers, FeatherClipboard, FeatherTag, FeatherMessageSquare, FeatherEye, FeatherCopy, FeatherTarget, FeatherX } from '../ui/icons';
import NotesPreview from './NotesPreview';
import CopyNotesModal from './CopyNotesModal';
import GoalTaskSelector from './GoalTaskSelector';
import GoalFeatureSelector from './GoalFeatureSelector';
import dailyReviewService from '../../services/dailyReviewService';
import { formatDateForDisplay } from '../../utils/date';
import './NotesBottomBar.css';

const NOTE_TYPES = {
    overallAssessment: {
        label: 'Overall',
        shortLabel: 'Overall',
        icon: <FeatherFileText size={14} />,
        placeholder: 'Enter your overall assessment of today\'s review...',
        required: true
    },
    issues: {
        label: 'Issues',
        shortLabel: 'Issues',
        icon: <FeatherAlertTriangle size={14} />,
        placeholder: 'List any issues or concerns identified...',
        required: false
    },
    actionItems: {
        label: 'Actions',
        shortLabel: 'Actions',
        icon: <FeatherCheckSquare size={14} />,
        placeholder: 'List any action items or follow-ups required...',
        required: false
    },
    resourceObservations: {
        label: 'Resources',
        shortLabel: 'Resources',
        icon: <FeatherUsers size={14} />,
        placeholder: 'Add observations about specific resources...',
        required: false
    },
    taskConcerns: {
        label: 'Tasks',
        shortLabel: 'Tasks',
        icon: <FeatherClipboard size={14} />,
        placeholder: 'Note any task-related concerns or blockers...',
        required: false
    },
    featureTagNotes: {
        label: 'Features',
        shortLabel: 'Features',
        icon: <FeatherTag size={14} />,
        placeholder: 'Add notes about feature tags and deadlines...',
        required: false
    },
    additionalComments: {
        label: 'Comments',
        shortLabel: 'Comments',
        icon: <FeatherMessageSquare size={14} />,
        placeholder: 'Any additional comments or observations...',
        required: false
    },
    goals: {
        label: 'Goals',
        shortLabel: 'Goals',
        icon: <FeatherTarget size={14} />,
        placeholder: 'Set your daily goals...',
        required: true,
        isSpecial: true // Special tab that doesn't use textarea
    }
};

const NotesBottomBar = ({ notes, onNotesChange, onExpandedChange, projectId, date }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState('overallAssessment');
    const [localNotes, setLocalNotes] = useState(notes || {});
    const [showPreview, setShowPreview] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [hasPreviousNotes, setHasPreviousNotes] = useState(false);
    const [previousNotesData, setPreviousNotesData] = useState(null);
    const [isCheckingNotes, setIsCheckingNotes] = useState(false);

    useEffect(() => {
        const notesWithGoals = notes || {};
        // Ensure goals structure exists
        if (!notesWithGoals.goals) {
            notesWithGoals.goals = {
                tasks: [],
                features: [],
                personal: [],
                noGoals: false
            };
        }
        setLocalNotes(notesWithGoals);
    }, [notes]);

    // Removed auto-expand logic - toolbar should start minimized
    // User can manually expand when needed

    useEffect(() => {
        // Notify parent of expanded state for padding adjustment
        if (onExpandedChange) {
            onExpandedChange(isExpanded);
        }
    }, [isExpanded, onExpandedChange]);

    // Check for previous notes availability
    useEffect(() => {
        if (projectId && date) {
            checkPreviousNotesAvailability();
        } else {
            setHasPreviousNotes(false);
            setPreviousNotesData(null);
        }
    }, [projectId, date]);

    const checkPreviousNotesAvailability = async () => {
        setIsCheckingNotes(true);
        try {
            const data = await dailyReviewService.getPreviousNotes(projectId, date);
            
            // Check if notes exist and have content
            const hasNotesContent = data && data.notes && Object.values(data.notes).some(note => note && note.trim() !== '');
            
            setHasPreviousNotes(hasNotesContent);
            setPreviousNotesData(data);
        } catch (error) {
            console.error('Error checking previous notes:', error);
            setHasPreviousNotes(false);
            setPreviousNotesData(null);
        } finally {
            setIsCheckingNotes(false);
        }
    };


    const handleNoteChange = (noteType, value) => {
        const updatedNotes = {
            ...localNotes,
            [noteType]: value
        };
        setLocalNotes(updatedNotes);
        onNotesChange(updatedNotes);
    };

    const handleAddTaskGoal = (taskGoal) => {
        const updatedNotes = {
            ...localNotes,
            goals: {
                ...localNotes.goals,
                tasks: [...(localNotes.goals?.tasks || []), taskGoal]
            }
        };
        setLocalNotes(updatedNotes);
        onNotesChange(updatedNotes);
    };

    const handleAddFeatureGoal = (featureGoal) => {
        const updatedNotes = {
            ...localNotes,
            goals: {
                ...localNotes.goals,
                features: [...(localNotes.goals?.features || []), featureGoal],
                noGoals: false // Unset noGoals if adding a goal
            }
        };
        setLocalNotes(updatedNotes);
        onNotesChange(updatedNotes);
    };

    const handleAddPersonalGoal = () => {
        const description = prompt('Enter your personal goal:');
        if (description && description.trim()) {
            const personalGoal = {
                id: `personal-${Date.now()}`,
                description: description.trim(),
                target: 'complete-today',
                status: 'pending',
                addedAt: new Date().toISOString()
            };
            const updatedNotes = {
                ...localNotes,
                goals: {
                    ...localNotes.goals,
                    personal: [...(localNotes.goals?.personal || []), personalGoal],
                    noGoals: false // Unset noGoals if adding a goal
                }
            };
            setLocalNotes(updatedNotes);
            onNotesChange(updatedNotes);
        }
    };

    const handleRemoveTaskGoal = (taskId) => {
        const updatedNotes = {
            ...localNotes,
            goals: {
                ...localNotes.goals,
                tasks: (localNotes.goals?.tasks || []).filter(g => g.taskId !== taskId),
                noGoals: false // Unset noGoals if removing a goal
            }
        };
        setLocalNotes(updatedNotes);
        onNotesChange(updatedNotes);
    };

    const handleRemoveFeatureGoal = (featureId) => {
        const updatedNotes = {
            ...localNotes,
            goals: {
                ...localNotes.goals,
                features: (localNotes.goals?.features || []).filter(g => g.featureId !== featureId),
                noGoals: false // Unset noGoals if removing a goal
            }
        };
        setLocalNotes(updatedNotes);
        onNotesChange(updatedNotes);
    };

    const handleRemovePersonalGoal = (goalId) => {
        const updatedNotes = {
            ...localNotes,
            goals: {
                ...localNotes.goals,
                personal: (localNotes.goals?.personal || []).filter(g => g.id !== goalId),
                noGoals: false // Unset noGoals if removing a goal
            }
        };
        setLocalNotes(updatedNotes);
        onNotesChange(updatedNotes);
    };

    const handleToggleNoGoals = () => {
        const currentNoGoals = localNotes.goals?.noGoals || false;
        const updatedNotes = {
            ...localNotes,
            goals: {
                ...localNotes.goals,
                noGoals: !currentNoGoals,
                // Clear all goals if marking as "no goals"
                tasks: !currentNoGoals ? [] : (localNotes.goals?.tasks || []),
                features: !currentNoGoals ? [] : (localNotes.goals?.features || []),
                personal: !currentNoGoals ? [] : (localNotes.goals?.personal || [])
            }
        };
        setLocalNotes(updatedNotes);
        onNotesChange(updatedNotes);
    };

    const hasNote = (noteType) => {
        if (noteType === 'goals') {
            const goals = localNotes.goals || {};
            // Goals are valid if there are goals set OR if "no goals" is explicitly marked
            return goals.noGoals === true || (goals.tasks?.length > 0) || (goals.features?.length > 0) || (goals.personal?.length > 0);
        }
        return localNotes[noteType] && localNotes[noteType].trim() !== '';
    };

    const getValidationStatus = () => {
        const hasOverallAssessment = hasNote('overallAssessment');
        const hasGoals = hasNote('goals');
        const totalNotes = Object.keys(NOTE_TYPES).filter(key => hasNote(key)).length;
        return {
            isValid: hasOverallAssessment && hasGoals,
            totalNotes,
            hasOverallAssessment,
            hasGoals
        };
    };

    const { isValid, totalNotes, hasOverallAssessment, hasGoals } = getValidationStatus();
    const activeNoteConfig = NOTE_TYPES[activeTab];
    const activeNoteValue = activeTab === 'goals' ? '' : (localNotes[activeTab] || '');

    const handleTabClick = (noteType) => {
        setActiveTab(noteType);
        if (!isExpanded) {
            setIsExpanded(true);
        }
    };

    const handleCopyFromPrevious = () => {
        if (!hasPreviousNotes || !previousNotesData) {
            return;
        }
        setShowCopyModal(true);
    };

    const handleConfirmCopy = (notesToCopy) => {
        // Filter goals: only copy goals that were not achieved
        const filteredNotes = {
            ...notesToCopy,
            goals: notesToCopy.goals ? {
                tasks: (notesToCopy.goals.tasks || []).filter(g => g.status !== 'achieved'),
                features: (notesToCopy.goals.features || []).filter(g => g.status !== 'achieved'),
                personal: (notesToCopy.goals.personal || []).filter(g => g.status !== 'achieved'),
                noGoals: notesToCopy.goals.noGoals || false
            } : { tasks: [], features: [], personal: [], noGoals: false }
        };
        
        // Remove status fields from goals (will be recalculated for the new day)
        if (filteredNotes.goals.tasks) {
            filteredNotes.goals.tasks = filteredNotes.goals.tasks.map(({ status, ...goal }) => goal);
        }
        if (filteredNotes.goals.features) {
            filteredNotes.goals.features = filteredNotes.goals.features.map(({ status, ...goal }) => goal);
        }
        if (filteredNotes.goals.personal) {
            filteredNotes.goals.personal = filteredNotes.goals.personal.map(({ status, ...goal }) => goal);
        }
        
        setLocalNotes(filteredNotes);
        onNotesChange(filteredNotes);
        setShowCopyModal(false);
        // Show success message (could use a toast notification instead)
        alert('Notes copied successfully! Achieved goals were automatically excluded.');
    };

    return (
        <div className={`notes-bottom-bar ${isExpanded ? 'expanded' : 'collapsed'}`}>
            {/* Header with Tabs */}
            <div className="notes-bottom-bar-header">
                <div className="notes-bottom-bar-tabs">
                    {Object.entries(NOTE_TYPES).map(([noteType, config]) => {
                        const hasValue = hasNote(noteType);
                        const isActive = activeTab === noteType;
                        return (
                            <button
                                key={noteType}
                                className={`notes-bottom-bar-tab ${isActive ? 'active' : ''} ${config.required && !hasValue ? 'required' : ''}`}
                                onClick={() => handleTabClick(noteType)}
                                title={config.label === 'Overall' ? 'Overall Assessment' : config.label}
                            >
                                <span className="notes-bottom-bar-tab-icon">
                                    {config.icon}
                                </span>
                                <span className="notes-bottom-bar-tab-label">
                                    {config.shortLabel}
                                </span>
                                {config.required && (
                                    <span className="notes-bottom-bar-tab-indicator">
                                        {hasValue ? '✓' : '*'}
                                    </span>
                                )}
                                {!config.required && hasValue && (
                                    <span className="notes-bottom-bar-tab-indicator">●</span>
                                )}
                            </button>
                        );
                    })}
                </div>
                <div className="notes-bottom-bar-header-right">
                    {projectId && date && (
                        <button
                            className="notes-bottom-bar-copy"
                            onClick={handleCopyFromPrevious}
                            disabled={!hasPreviousNotes || isCheckingNotes}
                            aria-label="Copy from previous day"
                            title={hasPreviousNotes ? "Copy notes from previous day" : "No previous notes available"}
                        >
                            <FeatherCopy size={16} />
                        </button>
                    )}
                    <button
                        className="notes-bottom-bar-preview"
                        onClick={() => setShowPreview(true)}
                        aria-label="Preview notes"
                        title="Preview all notes"
                    >
                        <FeatherEye size={16} />
                    </button>
                    <div className={`notes-bottom-bar-status ${isValid ? 'valid' : 'invalid'}`}>
                        {isValid ? (
                            <>
                                <FeatherCheckCircle size={14} />
                                <span>{totalNotes}</span>
                            </>
                        ) : (
                            <>
                                <FeatherAlertCircle size={14} />
                                <span>
                                    {!hasOverallAssessment ? 'Overall Assessment required' : !hasGoals ? 'Goals required' : 'Required fields missing'}
                                </span>
                            </>
                        )}
                    </div>
                    <button
                        className="notes-bottom-bar-toggle"
                        onClick={() => setIsExpanded(!isExpanded)}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                        {isExpanded ? <FeatherChevronDown size={16} /> : <FeatherChevronUp size={16} />}
                    </button>
                </div>
            </div>

            {/* Content Area (only when expanded) */}
            {isExpanded && (
                <div className="notes-bottom-bar-content">
                    {activeTab === 'goals' ? (
                        <div className="notes-goals-manager">
                            <div className="notes-goals-no-goals-option">
                                <label className="notes-goals-no-goals-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={localNotes.goals?.noGoals || false}
                                        onChange={handleToggleNoGoals}
                                    />
                                    <span>No goals for today</span>
                                </label>
                            </div>
                            
                            {!localNotes.goals?.noGoals && (
                                <>
                            <div className="notes-goals-section">
                                <h4 className="notes-goals-section-title">Task Goals</h4>
                                <GoalTaskSelector
                                    projectId={projectId}
                                    onSelect={handleAddTaskGoal}
                                    selectedTaskIds={(localNotes.goals?.tasks || []).map(g => g.taskId)}
                                />
                                <div className="notes-goals-list">
                                    {(localNotes.goals?.tasks || []).map(goal => (
                                        <div key={goal.taskId} className="notes-goal-item">
                                            <div className="notes-goal-content">
                                                <span className="notes-goal-badge">Complete Today</span>
                                                <span className="notes-goal-name">[{goal.displayId}] {goal.taskTitle}</span>
                                            </div>
                                            <button
                                                className="notes-goal-remove"
                                                onClick={() => handleRemoveTaskGoal(goal.taskId)}
                                                aria-label="Remove goal"
                                            >
                                                <FeatherX size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="notes-goals-section">
                                <h4 className="notes-goals-section-title">Feature Goals</h4>
                                <GoalFeatureSelector
                                    projectId={projectId}
                                    onSelect={handleAddFeatureGoal}
                                    selectedFeatureIds={(localNotes.goals?.features || []).map(g => g.featureId)}
                                />
                                <div className="notes-goals-list">
                                    {(localNotes.goals?.features || []).map(goal => (
                                        <div key={goal.featureId} className="notes-goal-item">
                                            <div className="notes-goal-content">
                                                <span className="notes-goal-badge">Complete Today</span>
                                                <span className="notes-goal-name">{goal.featureName}</span>
                                            </div>
                                            <button
                                                className="notes-goal-remove"
                                                onClick={() => handleRemoveFeatureGoal(goal.featureId)}
                                                aria-label="Remove goal"
                                            >
                                                <FeatherX size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="notes-goals-section">
                                <h4 className="notes-goals-section-title">Personal Goals</h4>
                                <button
                                    className="notes-goals-add-personal"
                                    onClick={handleAddPersonalGoal}
                                >
                                    + Add Personal Goal
                                </button>
                                <div className="notes-goals-list">
                                    {(localNotes.goals?.personal || []).map(goal => (
                                        <div key={goal.id} className="notes-goal-item">
                                            <div className="notes-goal-content">
                                                <span className="notes-goal-badge">Complete Today</span>
                                                <span className="notes-goal-name">{goal.description}</span>
                                            </div>
                                            <button
                                                className="notes-goal-remove"
                                                onClick={() => handleRemovePersonalGoal(goal.id)}
                                                aria-label="Remove goal"
                                            >
                                                <FeatherX size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="notes-bottom-bar-editor">
                            <label className="notes-bottom-bar-editor-label">
                                {activeNoteConfig.label === 'Overall' ? 'Overall Assessment' : activeNoteConfig.label}
                                {activeNoteConfig.required && <span className="required-asterisk">*</span>}
                            </label>
                            <textarea
                                className="notes-bottom-bar-textarea"
                                value={activeNoteValue}
                                onChange={(e) => handleNoteChange(activeTab, e.target.value)}
                                placeholder={activeNoteConfig.placeholder}
                                rows={10}
                                autoFocus
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Footer with Validation Status (always visible when expanded) */}
            {isExpanded && (
                <div className={`notes-bottom-bar-footer ${isValid ? 'valid' : 'invalid'}`}>
                    <div className="notes-bottom-bar-footer-status">
                        {isValid ? (
                            <>
                                <FeatherCheckCircle size={14} />
                                <span>Ready to complete ({totalNotes} notes)</span>
                            </>
                        ) : (
                            <>
                                <FeatherAlertCircle size={14} />
                                <span>
                                    {!hasOverallAssessment ? 'Overall Assessment required' : !hasGoals ? 'Goals required' : 'Required fields missing'}
                                </span>
                            </>
                        )}
                    </div>
                </div>
            )}

            {typeof document !== 'undefined' && createPortal(
                <NotesPreview
                    isOpen={showPreview}
                    onClose={() => setShowPreview(false)}
                    notes={localNotes}
                />,
                document.body
            )}
            {typeof document !== 'undefined' && createPortal(
                <CopyNotesModal
                    isOpen={showCopyModal}
                    onClose={() => setShowCopyModal(false)}
                    onConfirm={handleConfirmCopy}
                    sourceNotes={previousNotesData?.notes || {}}
                    sourceDate={previousNotesData?.date}
                    currentNotes={localNotes}
                />,
                document.body
            )}
        </div>
    );
};

export default NotesBottomBar;

